import { type ComponentProps, useMemo } from 'react'

import { CardGrid } from '@/components/common/CardGrid'
import { Subheader } from '@/components/common/Subheader'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { useAds } from '@/components/providers/AdsProvider'
import { useScrollspyRefs } from '@/components/providers/ScrollspyProvider'
import { useGridColumns } from '@/hooks/useGridColumns'
import { getAdIncontentIndexes } from '@/utils/adUtil'

import { useGalleryDisplay } from '../GalleryDisplayProvider/GalleryDisplayProvider'
import { GalleryEmpty } from '../GalleryEmpty/GalleryEmpty'
import { useGalleryGroups } from '../GalleryGroupsProvider/GalleryGroupsProvider'
import { GalleryPagination } from '../GalleryPagination/GalleryPagination'
import { GallerySubheader } from '../GallerySubheader/GallerySubheader'

import styles from './GalleryViewRecs.module.scss'
import { GalleryViewRecsJumpSidebar } from './GalleryViewRecsJumpSidebar/GalleryViewRecsJumpSidebar'

type Props = {
  variant?: ComponentProps<typeof CardGrid>['variant']
}

export const GalleryViewRecs = ({ variant }: Props) => {
  const { galleryIncontentDisabled } = useAds()
  const { groups, isEmpty } = useGalleryGroups()
  const { size } = useGalleryDisplay()
  const gridKey = useMemo(() => [groups, size, variant], [groups, size, variant])
  const { columns, ref: gridRef } = useGridColumns<HTMLUListElement>({
    activeKey: gridKey,
    enabled: !galleryIncontentDisabled,
  })
  const scrollspyRefs = useScrollspyRefs()

  const sections = useMemo(() => {
    const groupsVisible = groups.filter((group) => group.recs.length > 0)
    const indexes = getAdIncontentIndexes({
      columns,
      enabled: !galleryIncontentDisabled,
      groupLengths: groupsVisible.map((group) => group.recs.length),
    })

    return groupsVisible.map((group, i) => ({ ...group, incontentIndexes: indexes[i] ?? [] }))
  }, [columns, galleryIncontentDisabled, groups])

  return (
    <div className={styles.container}>
      <GalleryViewRecsJumpSidebar />
      <div className={styles.content}>
        <GallerySubheader variant='recs' />
        {isEmpty ? (
          <GalleryEmpty />
        ) : (
          sections.map((section, i) => (
            <div key={section.slug} ref={scrollspyRefs[section.slug]}>
              <Subheader id={section.slug} pushDown={i === 0} pushLeftDisabled={sections.length > 1}>
                <IconWithLabel iconBefore={section.icon} wrapNone>
                  {section.label}
                </IconWithLabel>
              </Subheader>
              <CardGrid
                assumeInViewCount={i === 0 ? 10 : 0}
                gridRef={i === 0 ? gridRef : undefined}
                incontentIndexes={section.incontentIndexes}
                recs={section.recs}
                size={size}
                variant={variant}
              />
            </div>
          ))
        )}
        <GalleryPagination />
      </div>
    </div>
  )
}
